import { useGlobalSearchParams } from 'expo-router';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type { ChatRecord } from '~/powersync/generated/schema';

export interface CurrentChat {
  /** The resolved conversation, or null while it loads / none exists yet. */
  chat: ChatRecord | null;
  /** The chat's uuid — the operational key for messages. Null until resolved. */
  chatId: string | null;
  /** The `niceId` from the URL (`/chat/:niceId`), or null on the bare `/chat`. */
  niceId: string | null;
  isLoading: boolean;
}

/**
 * Resolves the conversation the chat screen is currently showing, reading the
 * route as the single source of truth (so the chat window and the right-sidebar
 * panel — which render in separate subtrees — agree without a shared store).
 *
 * - On `/chat/:niceId` → the chat with that `niceId`.
 * - On the bare `/chat` → the most-recently-active chat (the landing route then
 *   redirects to its `niceId` once one syncs in; see `ChatIndexScreen`).
 *
 * Read-only: it never creates or navigates — those side-effects live solely in
 * the index route to avoid running once per subtree.
 *
 * Uses `useGlobalSearchParams` (not `useLocalSearchParams`): the chat-list panel
 * is rendered by the right-sidebar Outlet up at `SidebarLayout`, above the
 * `[niceId]` route segment, so only the globally-focused route's params reach
 * it. Reading globally is what lets both subtrees resolve the same chat.
 */
export const useCurrentChat = (): CurrentChat => {
  const { niceId: niceIdParam } = useGlobalSearchParams<{ niceId?: string }>();
  const niceId = niceIdParam ?? null;

  const { data: chat, isLoading } = useLivePowerSyncQuery<ChatRecord>(
    niceId
      ? db.Chat.queries.findByNiceId(niceId)
      : db.Chat.queries.findMostRecentActive(),
    [niceId],
    { single: true },
  );

  return {
    chat: chat ?? null,
    chatId: chat?.id ?? null,
    niceId,
    isLoading,
  };
};
